import React from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Separator } from "@/components/ui/separator";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Star,
  Download,
  Heart,
  MessageCircle,
  ExternalLink,
  Zap,
  ThumbsUp,
  Copy,
  Play,
} from "lucide-react";
import { mockWorkflowTemplates, categories } from "@/lib/flow-data";
import type { Flow } from '@/lib/types';
import { getFlowIcon } from '@/lib/flowIconMapping.tsx';

export function FlowDetailsDialog({
  selectedFlow,
  setSelectedFlow,
  renderStarRating,
  newComment,
  setNewComment,
  userRating,
  setUserRating,
}: {
  selectedFlow: Flow | null;
  setSelectedFlow: (flow: Flow | null) => void;
  renderStarRating: (rating: number, interactive?: boolean, onRatingChange?: (rating: number) => void) => React.ReactElement;
  newComment: string;
  setNewComment: (comment: string) => void;
  userRating: number;
  setUserRating: (rating: number) => void;
}) {
  return (
    <Dialog open={!!selectedFlow} onOpenChange={() => setSelectedFlow(null)}>
      <DialogContent
        className="w-[95vw] max-w-[95vw] md:max-h-[680px] h-[90vh] p-0 md:w-[85vw] md:max-w-[1200px] md:h-[85vh] overflow-hidden"
      >
        {selectedFlow && (
          <div className="flex flex-col md:flex-row h-full w-full overflow-y-auto md:overflow-hidden">
            {/* Mobile Header - Shows first on mobile */}
            <div className="block md:hidden border-b border-slate-200 dark:border-slate-700 p-4">
              <div className="space-y-4">
                {/* Flow header info */}
                <div className="flex items-center space-x-3">
                  <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-primary/10">
                    {React.createElement(getFlowIcon(selectedFlow.icon as string), { className: "h-6 w-6 text-primary" })}
                  </div>
                  <div className="flex-1">
                    <h2 className="text-xl font-bold text-slate-900 dark:text-white">
                      <Link
                        to={`/gallery/flow/${selectedFlow.id}`}
                        className="hover:text-green-600 dark:hover:text-green-400 transition-colors"
                      >
                        {selectedFlow.name}
                      </Link>
                    </h2>
                    <p className="text-sm text-slate-500 dark:text-slate-400">{selectedFlow.category}</p>
                  </div>
                </div>

                {/* Author and stats */}
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <Avatar className="h-8 w-8">
                      <AvatarImage src={selectedFlow.authorAvatar} />
                      <AvatarFallback>{selectedFlow.author?.[0] || 'U'}</AvatarFallback>
                    </Avatar>
                    <div>
                      <p className="text-sm font-medium text-slate-900 dark:text-white">{selectedFlow.author || 'Unknown Author'}</p>
                      <div className="flex items-center space-x-1">
                        {renderStarRating(selectedFlow.rating)}
                        <span className="text-xs text-slate-500 dark:text-slate-400">({selectedFlow.reviews})</span>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center space-x-1">
                    <Zap className="h-4 w-4 text-green-600" />
                    <span className="font-semibold text-green-600">{selectedFlow.credits} credits</span>
                  </div>
                </div>

                {/* Quick description */}
                <p className="text-sm text-slate-600 dark:text-slate-300 leading-relaxed">
                  {selectedFlow.description}
                </p>

                {/* Action buttons */}
                <div className="flex gap-2">
                  <Button className="flex-1 bg-green-600 hover:bg-green-700">
                    <Play className="h-4 w-4 mr-2" />
                    Add to Workflow
                  </Button>
                  <Button variant="outline" className="flex-1">
                    <Heart className="h-4 w-4 mr-2" />
                    Save
                  </Button>
                </div>
              </div>
            </div>

            {/* Tabs Content - Second on mobile, first on desktop */}
            <div className="w-full md:flex-1 order-2 md:order-1">
              <div className="h-auto md:h-full overflow-y-auto">
                <div className="p-4 md:p-6">
                  <Tabs defaultValue="overview" className="w-full">
                    <TabsList className="flex md:grid w-full md:grid-cols-5 mb-6 overflow-x-auto">
                      <TabsTrigger value="overview" className="flex-shrink-0">Overview</TabsTrigger>
                      <TabsTrigger value="inputs-outputs" className="flex-shrink-0">I/O</TabsTrigger>
                      <TabsTrigger value="documentation" className="flex-shrink-0">Docs</TabsTrigger>
                      <TabsTrigger value="comments" className="flex-shrink-0">Comments</TabsTrigger>
                      <TabsTrigger value="details" className="flex-shrink-0">Details</TabsTrigger>
                    </TabsList>

                    <TabsContent value="overview" className="space-y-4 mt-0">
                      <div>
                        <h3 className="text-lg font-semibold mb-2">Description</h3>
                        <p className="text-muted-foreground">{selectedFlow.description}</p>
                      </div>

                      <div>
                        <h3 className="text-lg font-semibold mb-2">Tags</h3>
                        <div className="flex flex-wrap gap-2">
                          {selectedFlow.tags.map((tag) => (
                            <Badge key={tag} variant="outline">
                              {tag}
                            </Badge>
                          ))}
                        </div>
                      </div>

                      {mockWorkflowTemplates[selectedFlow.id] && (
                        <div>
                          <h3 className="text-lg font-semibold mb-4">Workflow Templates Using This Flow</h3>
                          <div className="space-y-3">
                            {mockWorkflowTemplates[selectedFlow.id].map((workflow) => (
                              <div
                                key={workflow.id}
                                className="border rounded-lg p-4 hover:bg-muted/50 transition-colors cursor-pointer"
                              >
                                <div className="flex items-start space-x-4">
                                  <img
                                    src={workflow.thumbnail || "/placeholder.svg"}
                                    alt={`${workflow.name} thumbnail`}
                                    className="w-20 h-12 object-cover rounded border"
                                  />
                                  <div className="flex-1 min-w-0">
                                    <div className="flex items-start justify-between">
                                      <div>
                                        <h4 className="font-medium text-sm mb-1 truncate">{workflow.name}</h4>
                                        <p className="text-xs text-muted-foreground mb-2 line-clamp-2">
                                          {workflow.description}
                                        </p>
                                        <p className="text-xs text-muted-foreground">by {workflow.author}</p>
                                      </div>
                                      <div className="flex flex-col items-end text-xs text-muted-foreground ml-2">
                                        <div className="flex items-center space-x-1 mb-1">
                                          <Star className="h-3 w-3 fill-yellow-400 text-yellow-400" />
                                          <span>{workflow.rating}</span>
                                        </div>
                                        <span>{workflow.downloads} downloads</span>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                          <div className="mt-4">
                            <Button variant="outline" size="sm" className="w-full bg-transparent">
                              <ExternalLink className="h-4 w-4 mr-2" />
                              View All Workflows Using This Flow
                            </Button>
                          </div>
                        </div>
                      )}
                    </TabsContent>

                    <TabsContent value="inputs-outputs" className="space-y-6 mt-0">
                      <div>
                        <h3 className="text-lg font-semibold mb-4">Inputs</h3>
                        <div className="space-y-3">
                          <div className="text-center py-8 text-slate-500 dark:text-slate-400">
                            <p>Input specifications will be loaded dynamically</p>
                          </div>
                        </div>
                      </div>

                      <Separator />

                      <div>
                        <h3 className="text-lg font-semibold mb-4">Outputs</h3>
                        <div className="space-y-3">
                          <div className="text-center py-8 text-slate-500 dark:text-slate-400">
                            <p>Output specifications will be loaded dynamically</p>
                          </div>
                        </div>
                      </div>
                    </TabsContent>

                    <TabsContent value="documentation" className="mt-0">
                      <div className="prose prose-sm max-w-none">
                        <pre className="whitespace-pre-wrap text-sm">{selectedFlow.documentation}</pre>
                      </div>
                    </TabsContent>

                    <TabsContent value="comments" className="space-y-6 mt-0 max-h-[560px] overflow-y-auto">
                      <div>
                        <h3 className="text-lg font-semibold mb-4">Leave a Review</h3>
                        <div className="space-y-4 p-4 border rounded-lg">
                          <div>
                            <label className="text-sm font-medium mb-2 block">Your Rating</label>
                            {renderStarRating(userRating, true, setUserRating)}
                          </div>
                          <Textarea
                            placeholder="Share your experience with this Flow..."
                            value={newComment}
                            onChange={(e) => setNewComment(e.target.value)}
                          />
                          <Button>Post Review</Button>
                        </div>
                      </div>

                      <Separator />

                      <div>
                        <h3 className="text-lg font-semibold mb-4">Reviews ({selectedFlow.comments?.length || 0})</h3>
                        <div className="space-y-4">
                          {selectedFlow.comments?.map((comment) => (
                            <div key={comment.id} className="border rounded-lg p-4">
                              <div className="flex items-start justify-between mb-2">
                                <div className="flex items-center space-x-3">
                                  <Avatar className="h-8 w-8">
                                    <AvatarImage src={comment.avatar || "/placeholder.svg"} />
                                    <AvatarFallback>{comment.user[0]}</AvatarFallback>
                                  </Avatar>
                                  <div>
                                    <p className="font-medium">{comment.user}</p>
                                    <div className="flex items-center space-x-2">
                                      {renderStarRating(comment.rating)}
                                      <span className="text-sm text-muted-foreground">{comment.timestamp}</span>
                                    </div>
                                  </div>
                                </div>
                                <div className="flex items-center space-x-1 text-sm text-muted-foreground">
                                  <ThumbsUp className="h-4 w-4" />
                                  <span>{comment.likes}</span>
                                </div>
                              </div>
                              <p className="text-sm">{comment.comment}</p>
                            </div>
                          ))}
                        </div>
                      </div>
                    </TabsContent>

                    <TabsContent value="details" className="space-y-4 mt-0">
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <h4 className="font-medium mb-2">Version</h4>
                          <p className="text-sm text-muted-foreground">{selectedFlow.version}</p>
                        </div>
                        <div>
                          <h4 className="font-medium mb-2">Last Updated</h4>
                          <p className="text-sm text-muted-foreground">{selectedFlow.lastUpdated}</p>
                        </div>
                        <div>
                          <h4 className="font-medium mb-2">Downloads</h4>
                          <p className="text-sm text-muted-foreground">{selectedFlow.downloads.toLocaleString()}</p>
                        </div>
                        <div>
                          <h4 className="font-medium mb-2">Category</h4>
                          <p className="text-sm text-muted-foreground">{selectedFlow.category}</p>
                        </div>
                      </div>

                      <Separator className="mb-6" />

                      <div>
                        <h4 className="font-medium mb-2">Share Flow</h4>
                        <div className="flex space-x-2">
                          <Button variant="outline" size="sm">
                            <Copy className="h-4 w-4 mr-2" />
                            Copy Link
                          </Button>
                          <Link 
                            to={`/gallery/flow/${selectedFlow.id}`}
                            className="inline-flex items-center justify-center whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 border border-input bg-background hover:bg-accent hover:text-accent-foreground h-9 px-3"
                          >
                            <ExternalLink className="h-4 w-4 mr-2" />
                            Open Details
                          </Link>
                        </div>
                      </div>
                    </TabsContent>
                  </Tabs>
                </div>
              </div>
            </div>

            {/* Right Pane - Flow Info (Desktop Only) */}
            <div className="hidden md:flex w-100 border-l bg-muted/30 flex-col overflow-y-auto order-3 md:order-2">
              <div className="p-6 flex-1">
                {/* Flow header info */}
                <div className="flex items-center space-x-3 mb-4">
                  <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-primary/10">
                    {React.createElement(getFlowIcon(selectedFlow.icon as string), { className: "h-6 w-6 text-primary" })}
                  </div>
                  <div>
                    <h2 className="text-xl font-bold">
                      <Link
                        to={`/gallery/flow/${selectedFlow.id}`}
                        className="hover:text-green-600 transition-colors"
                      >
                        {selectedFlow.name}
                      </Link>
                    </h2>
                    <Badge variant="outline" className="mt-1">
                      {selectedFlow.category}
                    </Badge>
                  </div>
                </div>

                {/* Flow preview image */}
                <div className="mb-6">
                  <img
                    src={selectedFlow.image || "/placeholder.svg"}
                    alt={`${selectedFlow.name} preview`}
                    className="w-full h-40 object-cover rounded-lg"
                  />
                </div>

                {/* Author info */}
                <div className="flex items-center space-x-3 mb-4">
                  <Avatar className="h-8 w-8">
                    <AvatarImage src={selectedFlow.authorAvatar || "/placeholder.svg"} />
                    <AvatarFallback>{selectedFlow.author[0]}</AvatarFallback>
                  </Avatar>
                  <div>
                    <p className="font-medium">{selectedFlow.author || 'Unknown Author'}</p>
                    <p className="text-sm text-muted-foreground">Author</p>
                  </div>
                </div>

                {/* Credit cost and rating */}
                <div className="space-y-4 mb-6">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium">Credit Cost</span>
                    <div className="flex items-center space-x-1">
                      <Zap className="h-4 w-4 text-green-600" />
                      <span className="font-semibold text-green-600 text-lg">{selectedFlow.credits} credits</span>
                    </div>
                  </div>

                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium">Rating</span>
                    <div className="flex items-center space-x-2">
                      {renderStarRating(selectedFlow.rating)}
                      <span className="text-sm text-muted-foreground">({selectedFlow.rating})</span>
                    </div>
                  </div>

                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium">Downloads</span>
                    <span className="text-sm text-muted-foreground">{selectedFlow.downloads.toLocaleString()}</span>
                  </div>

                  {selectedFlow.featured && (
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium">Status</span>
                      <Badge variant="secondary" className="bg-accent text-accent-foreground">
                        Featured
                      </Badge>
                    </div>
                  )}
                </div>

                <Separator className="mb-6" />

                {/* Action buttons */}
                <div className="space-y-3">
                  <Button className="w-full bg-green-600 hover:bg-green-700">
                    <Play className="h-4 w-4 mr-2" />
                    Add to Workflow
                  </Button>
                  <div className="grid grid-cols-2 gap-2">
                    <Button variant="outline" size="sm">
                      <Heart className="h-4 w-4 mr-2" />
                      Save
                    </Button>
                    <Link 
                      to={`/gallery/flow/${selectedFlow.id}`}
                      className="inline-flex items-center justify-center whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 border border-input bg-background hover:bg-accent hover:text-accent-foreground h-9 px-3"
                    >
                      <Download className="h-4 w-4 mr-2" />
                      Share
                    </Link>
                  </div>
                  <Button variant="outline" className="w-full bg-transparent">
                    <MessageCircle className="h-4 w-4 mr-2" />
                    Contact Author
                  </Button>
                </div>
              </div>
            </div>

            {/* Mobile Details Section - Shows after tabs on mobile */}
            <div className="block md:hidden border-t border-slate-200 dark:border-slate-700 p-4 order-3">
              <div className="space-y-6">
                {/* Flow preview image */}
                <div>
                  <img
                    src={selectedFlow.image || "/placeholder.svg"}
                    alt={`${selectedFlow.name} preview`}
                    className="w-full h-40 object-cover rounded-lg"
                  />
                </div>

                {/* Detailed Stats */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="text-center p-3 bg-slate-50 dark:bg-slate-800 rounded-lg">
                    <Download className="h-4 w-4 mx-auto mb-1 text-slate-500" />
                    <p className="text-lg font-semibold text-slate-900 dark:text-white">{selectedFlow.downloads}</p>
                    <p className="text-xs text-slate-500 dark:text-slate-400">Downloads</p>
                  </div>
                  <Link
                    to={`/gallery/flow/${selectedFlow.id}#comments`}
                    className="text-center p-3 bg-slate-50 dark:bg-slate-800 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors cursor-pointer"
                  >
                    <MessageCircle className="h-4 w-4 mx-auto mb-1 text-slate-500" />
                    <p className="text-lg font-semibold text-slate-900 dark:text-white">{selectedFlow.comments?.length || 0}</p>
                    <p className="text-xs text-slate-500 dark:text-slate-400">Comments</p>
                  </Link>
                </div>

                {/* Tags */}
                <div>
                  <h4 className="font-medium mb-2 text-slate-900 dark:text-white">Tags</h4>
                  <div className="flex flex-wrap gap-1">
                    {selectedFlow.tags.map((tag) => (
                      <Badge key={tag} variant="secondary" className="text-xs">
                        {tag}
                      </Badge>
                    ))}
                  </div>
                </div>

                {/* Additional Actions */}
                <div className="space-y-2">
                  <Button variant="outline" className="w-full">
                    <Copy className="h-4 w-4 mr-2" />
                    Copy Link
                  </Button>
                  <Button variant="outline" className="w-full">
                    <MessageCircle className="h-4 w-4 mr-2" />
                    Contact Author
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
